import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StaffFeesService, CollectedFeeDay } from '../services/staff-fees.service';

declare var customToastr: any;

interface DayPoint {
  paymentDate: string;
  label: string;
  amount: number;
  x: number;
  y: number;
}

// Navigation params carried from the monthly overview screen — mirrors what
// openDetailedFee() passes in the mobile app, plus the monthYear string
// (e.g. "September 2026") reparsed here into monthNumber/yearNumber for
// the day-wise API call.
export interface StaffFeesMonthlyNavState {
  staffId: number | string;
  academicId: number | string;
  month: string;
  currencySymbol: string;
  enabledLateFees: boolean;
  graphStatus: boolean;
}

const CRORE = 1_00_00_000;
const LAKH = 1_00_000;
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

@Component({
  selector: 'app-staff-fees-monthly',
  templateUrl: './staff-fees-monthly.component.html',
  styleUrls: ['../shared/staff-common.css', '../fees/staff-fees.component.css', './staff-fees-monthly.component.css']
})
export class StaffFeesMonthlyComponent implements OnInit {
  staffId: number | string | null = null;
  academicId: number | string | null = null;
  month = '';
  currencySymbol = '';

  feesData: CollectedFeeDay[] = [];
  fetchingData = true;

  // Both toggles are inherited from the monthly overview screen rather than
  // defaulting fresh, so the drill-down matches what the user was just
  // looking at — graphStatus defaults to true unless it was exactly false.
  openGraph = true;
  applyLateFees = false;

  readonly skeletonTiles = Array.from({ length: 16 }, () => ({}));

  constructor(
    private router: Router,
    private staffFeesService: StaffFeesService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Router.navigate's { state } extras land in history.state by the time
    // this component activates — getCurrentNavigation() is only valid
    // synchronously during the navigation itself, not reliably in ngOnInit.
    const state = history.state as StaffFeesMonthlyNavState;

    if (!state?.staffId || !state?.academicId || !state?.month) {
      this.router.navigate(['/staff/fees']);
      return;
    }

    this.staffId = state.staffId;
    this.academicId = state.academicId;
    this.month = state.month;
    this.currencySymbol = state.currencySymbol || '';
    this.openGraph = state.graphStatus === false ? false : true;
    this.applyLateFees = !!state.enabledLateFees;

    this.loadFeesData();
  }

  goBack() {
    this.router.navigate(['/staff/fees']);
  }

  private async loadFeesData() {
    if (!this.staffId || !this.academicId || !this.month) { return; }
    const [monthName, yearNumber] = this.month.split(' ');
    const monthNumber = MONTH_NAMES.findIndex(m => m === monthName) + 1;

    this.fetchingData = true;
    try {
      const response = await firstValueFrom(
        this.staffFeesService.getMonthFeesInfo(this.staffId, this.academicId, monthNumber, yearNumber)
      );
      this.feesData = response?.status ? (response.body?.collectedFeesDayWise || []) : [];
    } catch {
      this.feesData = [];
      const message = await firstValueFrom(this.translate.get('somethingWentWrong'));
      customToastr(message, 'error');
    } finally {
      this.fetchingData = false;
    }
  }

  toggleGraph() {
    this.openGraph = !this.openGraph;
  }

  toggleLateFees() {
    this.applyLateFees = !this.applyLateFees;
  }

  private amountFor(day: CollectedFeeDay): number {
    return this.applyLateFees ? day.amountCollectedWithLateFees : day.amountCollected;
  }

  dayAmount(day: CollectedFeeDay): number {
    return this.amountFor(day);
  }

  private formatShorthand(value: number): string {
    if (!value) { return `${this.currencySymbol}0`; }
    if (value >= CRORE) { return `${+(value / CRORE).toFixed(1)}Cr`; }
    if (value >= LAKH) { return `${+(value / LAKH).toFixed(1)}L`; }
    return `${+(value / 1000).toFixed(1)}k`;
  }

  formatAmount(value: number): string {
    return `${this.currencySymbol}${(value || 0).toLocaleString('en-IN')}`;
  }

  get maxValue(): number {
    return this.feesData.reduce((max, d) => Math.max(max, this.amountFor(d)), 0);
  }

  get yAxisLabels(): string[] {
    const max = this.maxValue;
    if (!max) { return ['0', '0', '0', '0', '0', '0']; }
    const step = max / 5;
    return Array.from({ length: 6 }, (_, i) => this.formatShorthand(step * i)).reverse();
  }

  private readonly minPointSpacing = 60;
  readonly chartHeight = 200;

  get chartWidth(): number {
    const count = this.feesData.length;
    return Math.max(600, count * this.minPointSpacing);
  }

  get chartPoints(): DayPoint[] {
    const max = this.maxValue || 1;
    const width = this.chartWidth;
    const height = this.chartHeight;
    const margin = 16;
    const count = this.feesData.length;
    const stepX = count > 1 ? (width - margin * 2) / (count - 1) : 0;
    return this.feesData.map((d, i) => {
      const amount = this.amountFor(d);
      return {
        paymentDate: d.paymentDate,
        label: d.paymentDate,
        amount,
        x: margin + i * stepX,
        y: height - margin - (amount / max) * (height - margin * 2),
      };
    });
  }

  get chartLinePoints(): string {
    return this.chartPoints.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  }

}
